import React, { useState, useEffect } from 'react';
import { format, parseISO, isAfter, isBefore, isEqual } from 'date-fns';
import { transactionData, accountTypes, transactionTypes, ownersList } from '../data/transactions';
import { FinancialTransaction } from '../types';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Trash2, Plus, Edit, X, Check, AlertCircle, Save, DollarSign, ArrowUpRight, ArrowDownLeft, RefreshCw } from 'lucide-react';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#d0ed57', '#ff6b6b'];
const STORAGE_KEY = 'financialDashboard_transactions';

const FinancialTransactions: React.FC = () => {
  // Load transactions from localStorage or use default data
  const loadTransactionsFromStorage = (): FinancialTransaction[] => {
    const savedTransactions = localStorage.getItem(STORAGE_KEY);
    if (savedTransactions) {
      try {
        return JSON.parse(savedTransactions);
      } catch (error) {
        console.error('Error parsing saved transactions:', error);
        return transactionData;
      }
    }
    return transactionData;
  };

  const [transactions, setTransactions] = useState<FinancialTransaction[]>(loadTransactionsFromStorage);
  const [selectedType, setSelectedType] = useState<string>('all');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd'),
    end: format(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0), 'yyyy-MM-dd')
  });
  const [isAddingTransaction, setIsAddingTransaction] = useState<boolean>(false);
  const [isEditingTransaction, setIsEditingTransaction] = useState<boolean>(false);
  const [editingTransactionId, setEditingTransactionId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  
  // Form state for new transaction
  const [newTransaction, setNewTransaction] = useState<Partial<FinancialTransaction>>({
    date: format(new Date(), 'yyyy-MM-dd'),
    type: 'owner_draw',
    amount: 0,
    description: '',
    fromAccount: 'Operating',
    toAccount: '',
    recipient: ''
  });
  
  // Filter transactions based on selected type and date range
  const filteredTransactions = transactions.filter(transaction => {
    const typeMatch = selectedType === 'all' || transaction.type === selectedType;
    const transactionDate = parseISO(transaction.date);
    const startDate = parseISO(dateRange.start);
    const endDate = parseISO(dateRange.end);
    
    const dateMatch = (
      (isAfter(transactionDate, startDate) || isEqual(transactionDate, startDate)) &&
      (isBefore(transactionDate, endDate) || isEqual(transactionDate, endDate))
    );
    
    return typeMatch && dateMatch;
  });
  
  // Calculate totals by type
  const getTotalsByType = () => {
    const totals: { [key: string]: number } = {
      owner_draw: 0,
      loan_payback: 0,
      transfer: 0,
      other: 0
    };
    
    filteredTransactions.forEach(transaction => {
      totals[transaction.type] += transaction.amount;
    });
    
    return totals;
  };
  
  // Calculate total amount of filtered transactions
  const totalAmount = filteredTransactions.reduce((sum, transaction) => sum + transaction.amount, 0);
  
  // Save transactions to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(transactions));
  }, [transactions]);
  
  // Reset form when adding new transaction
  const resetForm = () => {
    setNewTransaction({
      date: format(new Date(), 'yyyy-MM-dd'),
      type: 'owner_draw',
      amount: 0,
      description: '',
      fromAccount: 'Operating',
      toAccount: '',
      recipient: ''
    });
    setFormError(null);
  };
  
  // Handle adding a new transaction
  const handleAddTransaction = () => {
    // Validate form
    if (!newTransaction.description || newTransaction.description.trim() === '') {
      setFormError('Description is required');
      return;
    }
    
    if (!newTransaction.amount || newTransaction.amount <= 0) {
      setFormError('Amount must be greater than 0');
      return;
    }
    
    if (newTransaction.type === 'owner_draw' && (!newTransaction.recipient || newTransaction.recipient.trim() === '')) {
      setFormError('Recipient is required for owner draws');
      return;
    }
    
    if (newTransaction.type === 'transfer' && (!newTransaction.toAccount || newTransaction.toAccount.trim() === '')) {
      setFormError('To Account is required for transfers');
      return;
    }
    
    if (newTransaction.type === 'loan_payback' && (!newTransaction.toAccount || newTransaction.toAccount.trim() === '')) {
      setFormError('To Account is required for loan paybacks');
      return;
    }
    
    // Create new transaction with unique ID
    const maxId = transactions.length > 0 
      ? Math.max(...transactions.map(t => t.id)) 
      : 0;
    
    const transactionToAdd: FinancialTransaction = {
      id: maxId + 1,
      date: newTransaction.date || format(new Date(), 'yyyy-MM-dd'),
      type: newTransaction.type as 'owner_draw' | 'loan_payback' | 'transfer' | 'other',
      amount: newTransaction.amount || 0,
      description: newTransaction.description || '',
      fromAccount: newTransaction.fromAccount,
      toAccount: newTransaction.toAccount,
      recipient: newTransaction.recipient
    };
    
    // Add to transactions list
    const updatedTransactions = [...transactions, transactionToAdd];
    setTransactions(updatedTransactions);
    
    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedTransactions));
    
    // Show success message briefly
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
    
    // Reset form and close modal
    resetForm();
    setIsAddingTransaction(false);
  };
  
  // Handle editing a transaction
  const handleEditTransaction = (transaction: FinancialTransaction) => {
    setEditingTransactionId(transaction.id);
    setIsEditingTransaction(true);
    setNewTransaction({
      date: transaction.date,
      type: transaction.type,
      amount: transaction.amount,
      description: transaction.description,
      fromAccount: transaction.fromAccount,
      toAccount: transaction.toAccount,
      recipient: transaction.recipient
    });
  };
  
  // Handle saving edited transaction
  const handleSaveEdit = () => {
    // Validate form
    if (!newTransaction.description || newTransaction.description.trim() === '') {
      setFormError('Description is required');
      return;
    }
    
    if (!newTransaction.amount || newTransaction.amount <= 0) {
      setFormError('Amount must be greater than 0');
      return;
    }
    
    if (newTransaction.type === 'owner_draw' && (!newTransaction.recipient || newTransaction.recipient.trim() === '')) {
      setFormError('Recipient is required for owner draws');
      return;
    }
    
    if (newTransaction.type === 'transfer' && (!newTransaction.toAccount || newTransaction.toAccount.trim() === '')) {
      setFormError('To Account is required for transfers');
      return;
    }
    
    if (newTransaction.type === 'loan_payback' && (!newTransaction.toAccount || newTransaction.toAccount.trim() === '')) {
      setFormError('To Account is required for loan paybacks');
      return;
    }
    
    // Update the transaction
    const updatedTransactions = transactions.map(transaction => {
      if (transaction.id === editingTransactionId) {
        return {
          ...transaction,
          date: newTransaction.date || transaction.date,
          type: (newTransaction.type as 'owner_draw' | 'loan_payback' | 'transfer' | 'other') || transaction.type,
          amount: newTransaction.amount || transaction.amount,
          description: newTransaction.description || transaction.description,
          fromAccount: newTransaction.fromAccount,
          toAccount: newTransaction.toAccount,
          recipient: newTransaction.recipient
        };
      }
      return transaction;
    });
    
    setTransactions(updatedTransactions);
    
    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedTransactions));
    
    // Show success message briefly
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
    
    // Reset form and close modal
    resetForm();
    setIsEditingTransaction(false);
    setEditingTransactionId(null);
  };
  
  // Handle deleting a transaction
  const handleDeleteTransaction = (id: number) => {
    const updatedTransactions = transactions.filter(transaction => transaction.id !== id);
    setTransactions(updatedTransactions);
    
    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedTransactions));
    
    // Show success message briefly
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
    
    setShowDeleteConfirm(null);
  };
  
  // Handle manual save to localStorage
  const handleManualSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(transactions));
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };
  
  // Calculate data for charts
  const getPieData = () => {
    const totals = getTotalsByType();
    
    return Object.entries(totals)
      .filter(([_, value]) => value > 0)
      .map(([key, value]) => ({
        name: key === 'owner_draw' ? 'Owner Draws' :
              key === 'loan_payback' ? 'Loan Paybacks' :
              key === 'transfer' ? 'Transfers' : 'Other',
        value
      }));
  };
  
  // Get data for owner draws breakdown
  const getOwnerDrawsData = () => {
    const ownerTotals: { [key: string]: number } = {};
    
    filteredTransactions
      .filter(transaction => transaction.type === 'owner_draw')
      .forEach(transaction => {
        const recipient = transaction.recipient || 'Unknown';
        if (ownerTotals[recipient]) {
          ownerTotals[recipient] += transaction.amount;
        } else {
          ownerTotals[recipient] = transaction.amount;
        }
      });
    
    return Object.entries(ownerTotals).map(([name, value]) => ({
      name,
      value
    }));
  };
  
  // Get data for loan paybacks breakdown
  const getLoanPaybacksData = () => {
    const loanTotals: { [key: string]: number } = {};
    
    filteredTransactions
      .filter(transaction => transaction.type === 'loan_payback')
      .forEach(transaction => {
        const loanAccount = transaction.toAccount || 'Unknown';
        if (loanTotals[loanAccount]) {
          loanTotals[loanAccount] += transaction.amount;
        } else {
          loanTotals[loanAccount] = transaction.amount;
        }
      });
    
    return Object.entries(loanTotals).map(([name, value]) => ({
      name,
      value
    }));
  };
  
  // Get icon for transaction type
  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'owner_draw':
        return <DollarSign className="h-5 w-5 text-red-500" />;
      case 'loan_payback':
        return <ArrowUpRight className="h-5 w-5 text-blue-500" />;
      case 'transfer':
        return <RefreshCw className="h-5 w-5 text-green-500" />;
      default:
        return <ArrowDownLeft className="h-5 w-5 text-gray-500" />;
    }
  };
  
  // Get label for transaction type
  const getTransactionTypeLabel = (type: string) => {
    switch (type) {
      case 'owner_draw':
        return 'Owner Draw';
      case 'loan_payback':
        return 'Loan Payback';
      case 'transfer':
        return 'Transfer';
      default:
        return 'Other';
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Financial Transactions</h1>
        <div className="flex gap-2">
          <button
            onClick={handleManualSave}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center"
            title="Save all changes to browser storage"
          >
            <Save className="h-5 w-5 mr-1" />
            Save Changes
          </button>
          <button
            onClick={() => {
              resetForm();
              setIsAddingTransaction(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center"
          >
            <Plus className="h-5 w-5 mr-1" />
            Add Transaction
          </button>
        </div>
      </div>
      
      {/* Success message */}
      {saveSuccess && (
        <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded-md flex items-center">
          <Check className="h-5 w-5 mr-2" />
          Changes saved successfully!
        </div>
      )}
      
      {/* Add/Edit Transaction Modal */}
      {(isAddingTransaction || isEditingTransaction) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {isAddingTransaction ? 'Add New Transaction' : 'Edit Transaction'}
              </h2>
              <button
                onClick={() => {
                  resetForm();
                  setIsAddingTransaction(false);
                  setIsEditingTransaction(false);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {formError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                {formError}
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transaction Date*
                </label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newTransaction.date || format(new Date(), 'yyyy-MM-dd')}
                  onChange={(e) => setNewTransaction({ ...newTransaction, date: e.target.value })}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transaction Type*
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newTransaction.type || 'owner_draw'}
                  onChange={(e) => setNewTransaction({ ...newTransaction, type: e.target.value as any })}
                >
                  {transactionTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount*
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                    $
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={newTransaction.amount || ''}
                    onChange={(e) => setNewTransaction({ ...newTransaction, amount: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Account*
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newTransaction.fromAccount || ''}
                  onChange={(e) => setNewTransaction({ ...newTransaction, fromAccount: e.target.value })}
                >
                  {accountTypes.map(account => (
                    <option key={account} value={account}>{account}</option>
                  ))}
                </select>
              </div>
              
              {(newTransaction.type === 'transfer' || newTransaction.type === 'loan_payback') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    To Account*
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={newTransaction.toAccount || ''}
                    onChange={(e) => setNewTransaction({ ...newTransaction, toAccount: e.target.value })}
                  >
                    <option value="">Select Account</option>
                    {accountTypes.map(account => (
                      <option key={account} value={account}>{account}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {newTransaction.type === 'owner_draw' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recipient*
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={newTransaction.recipient || ''}
                    onChange={(e) => setNewTransaction({ ...newTransaction, recipient: e.target.value })}
                  >
                    <option value="">Select Recipient</option>
                    {ownersList.map(owner => (
                      <option key={owner} value={owner}>{owner}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description*
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newTransaction.description || ''}
                  onChange={(e) => setNewTransaction({ ...newTransaction, description: e.target.value })}
                  placeholder="e.g., Monthly owner draw"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  resetForm();
                  setIsAddingTransaction(false);
                  setIsEditingTransaction(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
              >
                Cancel
              </button>
              
              <button
                onClick={isAddingTransaction ? handleAddTransaction : handleSaveEdit}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center"
              >
                <Check className="h-5 w-5 mr-1" />
                {isAddingTransaction ? 'Add Transaction' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Transaction Overview</h2>
          <div className="mt-4 md:mt-0 flex flex-wrap gap-2">
            <select
              className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="all">All Transaction Types</option>
              {transactionTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            
            <div className="flex items-center gap-2">
              <input
                type="date"
                className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              />
              <span>to</span>
              <input
                type="date"
                className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              />
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-500">Total Transactions</p>
            <p className="text-2xl font-bold">${totalAmount.toLocaleString()}</p>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-500">Owner Draws</p>
            <p className="text-2xl font-bold text-red-600">${getTotalsByType().owner_draw.toLocaleString()}</p>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-500">Loan Paybacks</p>
            <p className="text-2xl font-bold text-blue-600">${getTotalsByType().loan_payback.toLocaleString()}</p>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-500">Transfers</p>
            <p className="text-2xl font-bold text-green-600">${getTotalsByType().transfer.toLocaleString()}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Transaction Distribution</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={getPieData()}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {getPieData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          <div>
            {selectedType === 'owner_draw' || selectedType === 'all' ? (
              <>
                <h3 className="text-lg font-semibold mb-2">Owner Draws Breakdown</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={getOwnerDrawsData()}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                      <Legend />
                      <Bar dataKey="value" name="Amount" fill="#FF8042" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
            ) : selectedType === 'loan_payback' ? (
              <>
                <h3 className="text-lg font-semibold mb-2">Loan Paybacks Breakdown</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={getLoanPaybacksData()}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                      <Legend />
                      <Bar dataKey="value" name="Amount" fill="#0088FE" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-80 bg-gray-50 rounded-lg">
                <p className="text-gray-500">Select "All" or "Owner Draws" or "Loan Paybacks" to see breakdown</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-4">Transaction History</h2>
        
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Date
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Type
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Description
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Amount
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  From
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  To/Recipient
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.length > 0 ? (
                filteredTransactions.map((transaction, index) => (
                  <tr key={transaction.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="py-2 px-4 border-b border-gray-200">
                      {format(parseISO(transaction.date), 'MMM dd, yyyy')}
                    </td>
                    <td className="py-2 px-4 border-b border-gray-200">
                      <div className="flex items-center">
                        {getTransactionIcon(transaction.type)}
                        <span className="ml-2">{getTransactionTypeLabel(transaction.type)}</span>
                      </div>
                    </td>
                    <td className="py-2 px-4 border-b border-gray-200 font-medium">{transaction.description}</td>
                    <td className="py-2 px-4 border-b border-gray-200 font-semibold">${transaction.amount.toLocaleString()}</td>
                    <td className="py-2 px-4 border-b border-gray-200">{transaction.fromAccount}</td>
                    <td className="py-2 px-4 border-b border-gray-200">
                      {transaction.type === 'owner_draw' ? transaction.recipient : transaction.toAccount}
                    </td>
                    <td className="py-2 px-4 border-b border-gray-200">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditTransaction(transaction)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        
                        {showDeleteConfirm === transaction.id ? (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleDeleteTransaction(transaction.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Confirm Delete"
                            >
                              <Check className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => setShowDeleteConfirm(null)}
                              className="text-gray-600 hover:text-gray-800"
                              title="Cancel"
                            >
                              <X className="h-5 w-5" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setShowDeleteConfirm(transaction.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="py-4 px-4 text-center text-gray-500">
                    No transactions found for the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot className="bg-gray-50 font-semibold">
              <tr>
                <td colSpan={3} className="py-2 px-4 border-t border-gray-200 text-right">
                  Total:
                </td>
                <td className="py-2 px-4 border-t border-gray-200">
                  ${totalAmount.toLocaleString()}
                </td>
                <td colSpan={3} className="py-2 px-4 border-t border-gray-200"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FinancialTransactions;